import {useMutation} from '@tanstack/react-query'

import {
  createRobot,
  deleteRobot,
  getRobots,
  getRoles,
  Membership,
  Robot,
  Role,
} from '@sanity/access-api'
import {useQuery} from '@tanstack/react-query'
import {useRouter} from 'next/router'
import {queryClient} from '@/data/queryclient'

export const useOrgRoles = () => {
  const {
    query: {orgId: organizationId},
  } = useRouter()

  return useQuery({
    queryKey: ['org-roles', organizationId],
    queryFn: () => {
      return getRoles({
        path: {
          resourceType: 'organization',
          resourceId: organizationId as string,
        },
        query: {
          includeChildren: true,
        },
      })
    },
    enabled: !!organizationId,
    select: (data) => {
      return data.data.data.filter((role) => role.appliesToRobots) as Role[]
    },
  })
}

export const useProjectRoles = (projectId: string) => {
  const {
    query: {orgId: organizationId},
  } = useRouter()

  return useQuery({
    queryKey: ['project-roles', organizationId],
    queryFn: () => {
      return getRoles({
        path: {
          resourceType: 'project',
          resourceId: projectId,
        },
        query: {
          includeChildren: false,
        },
      })
    },
    enabled: !!organizationId,
    select: (data) => {
      return data.data.data.filter((role) => role.appliesToRobots) as Role[]
    },
  })
}

export const useOrgTokens = () => {
  const {
    query: {orgId: organizationId},
  } = useRouter()

  return useQuery({
    queryKey: ['org-tokens', organizationId],
    queryFn: () => {
      return getRobots({
        path: {
          resourceType: 'organization',
          resourceId: organizationId as string,
        },
        query: {
          includeChildren: true,
        },
      })
    },
    enabled: !!organizationId,
    select: (data) => ({
      ...data.data,
      data: data.data.data.sort(
        (a, b) => +new Date(b.createdAt as string) - +new Date(a.createdAt as string)
      ),
    }),
  })
}

export const useCreateOrgToken = () => {
  const {
    query: {orgId: organizationId},
  } = useRouter()

  return useMutation({
    mutationFn: ({label, memberships}: {label: string; memberships: Membership[]}) =>
      createRobot({
        path: {
          resourceType: 'organization',
          resourceId: organizationId as string,
        },
        body: {
          label,
          memberships,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['org-tokens', organizationId]})
    },
  })
}

export const useDeleteOrgToken = () => {
  const {
    query: {orgId: organizationId},
  } = useRouter()
  return useMutation({
    mutationFn: (token: Robot) => {
      const orgMemberships = token.memberships.filter(
        (membership) => membership.resourceType === 'organization'
      )
      const projectMemberships = token.memberships.filter(
        (membership) => membership.resourceType === 'project'
      )

      const resourceType = orgMemberships.length > 0 ? 'organization' : 'project'
      const resourceId =
        orgMemberships.length > 0 ? organizationId : projectMemberships[0].resourceId
      return deleteRobot({
        path: {
          resourceType,
          resourceId: resourceId as string,
          robotId: token.id,
        },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['org-tokens', organizationId]})
    },
  })
}
